import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { SpendKpiDashboard } from "@/components/inventory/spend-kpi-dashboard";
import { CategoriesTab } from "@/components/inventory/categories-tab";
import { VendorsTab } from "@/components/inventory/vendors-tab";
import { ItemsTab } from "@/components/inventory/items-tab";
import { PlansTab } from "@/components/inventory/plans-tab";
import { EntitlementsTab } from "@/components/inventory/entitlements-tab";
import { MembersTab } from "@/components/inventory/members-tab";
import { AuditLogTab } from "@/components/inventory/audit-log-tab";
import { HsnCodesTab } from "@/components/inventory/hsn-codes-tab";
import { ChargeTypesTab } from "@/components/inventory/charge-types-tab";

type TabValue =
  | "categories"
  | "vendors"
  | "items"
  | "plans"
  | "entitlements"
  | "members"
  | "hsn-codes"
  | "charge-types"
  | "audit-log";

const TAB_CTA: Record<TabValue, string | null> = {
  categories: "Add Category",
  vendors: "Add Vendor",
  items: "Add Item",
  plans: "Add Plan",
  entitlements: "Add Entitlement",
  members: "Add Member",
  "hsn-codes": "Add HSN Code",
  "charge-types": "Add Charge Type",
  "audit-log": null,
};

const TAB_VALUES: TabValue[] = [
  "categories", "vendors", "items", "plans", "entitlements", "members", "hsn-codes", "charge-types", "audit-log",
];

export default function Inventory() {
  const [location] = useLocation();
  // Deep-link support: /inventory/<tab> opens that tab (used by the sidebar shortcuts).
  const seg = location.split("/").pop() ?? "";
  const initialTab: TabValue = (TAB_VALUES as string[]).includes(seg) ? (seg as TabValue) : "categories";
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);
  const [openCreate, setOpenCreate] = useState(false);

  // Sync the active tab when the URL changes (e.g. clicking a sidebar shortcut while mounted).
  useEffect(() => {
    if ((TAB_VALUES as string[]).includes(seg)) setActiveTab(seg as TabValue);
  }, [seg]);

  // 2026-07-22 — bare /inventory is dashboard-only (KPI card); master-data
  // tabs render only when the operator arrives via a deep-link like
  // /inventory/plans or /inventory/hsn-codes from the sidebar. Prevents
  // the duplicated Categories/Vendors/Items rendering that also lives on
  // /inventory/operation.
  const isDashboardOnly = !(TAB_VALUES as string[]).includes(seg);

  const ctaLabel = TAB_CTA[activeTab];

  return (
    <div className="space-y-6">
      {/* Bare /inventory renders the Spend Dashboard (own header). Deep-link
          tabs still show the traditional master-data header. */}
      {isDashboardOnly ? (
        <SpendKpiDashboard />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Inventory</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Master data — categories, vendors, items, plans, and members.
              </p>
            </div>
            {ctaLabel && <Button onClick={() => setOpenCreate(true)}>{ctaLabel}</Button>}
          </div>
        </>
      )}
      {isDashboardOnly ? null : (
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as TabValue); setOpenCreate(false); }}>
        <TabsList className="grid grid-cols-9 w-full">
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="entitlements">Entitlements</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="hsn-codes">HSN / GST</TabsTrigger>
          <TabsTrigger value="charge-types">Charges</TabsTrigger>
          <TabsTrigger value="audit-log">Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="categories">
          <CategoriesTab
            openCreate={activeTab === "categories" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="vendors">
          <VendorsTab
            openCreate={activeTab === "vendors" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="items">
          <ItemsTab
            openCreate={activeTab === "items" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="plans">
          <PlansTab
            openCreate={activeTab === "plans" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="entitlements">
          <EntitlementsTab
            openCreate={activeTab === "entitlements" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="members">
          <MembersTab
            openCreate={activeTab === "members" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="hsn-codes">
          <HsnCodesTab
            openCreate={activeTab === "hsn-codes" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="charge-types">
          <ChargeTypesTab
            openCreate={activeTab === "charge-types" && openCreate}
            setOpenCreate={setOpenCreate}
          />
        </TabsContent>
        <TabsContent value="audit-log">
          <AuditLogTab />
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}
